import { useEffect, useRef, useState } from "react";

const painPoints = [
  {
    emoji: "📊",
    title: "Marges découvertes trop tard",
    description: "Vous ne savez si un chantier est rentable qu'une fois terminé. Trop tard pour corriger.",
    color: "#EF4444",
    hasBadge: true,
  },
  {
    emoji: "📄",
    title: "Factures éparpillées",
    description: "Mails, factures papier, tickets de caisse... Impossible de savoir où en est votre budget.",
    color: "#F59E0B",
    hasBadge: false,
  },
  {
    emoji: "⏱️",
    title: "Retards non anticipés",
    description: "Un retard de 5 jours peut effacer toute votre marge. Sans outil, vous le découvrez trop tard.",
    color: "#D4AF37",
    hasBadge: false,
  },
  {
    emoji: "👥",
    title: "Coûts d'équipe flous",
    description: "Vous ne savez pas combien vous coûte réellement chaque membre sur chaque chantier.",
    color: "#FFFFFF",
    hasBadge: false,
  },
];

// Timing de l'animation mèche
const INITIAL_DELAY = 200;
const STEP_INTERVAL = 700;
const LINE_DURATION = 700;

function ItemContent({ point, align }: { point: typeof painPoints[0]; align: "left" | "right" }) {
  return (
    <div className={align === "left" ? "text-right" : "text-left"}>
      <h3 className="text-lg font-semibold text-white mb-2">
        {point.emoji} {point.title}
      </h3>
      <p className="text-sm text-white/40 leading-relaxed">
        {point.description}
      </p>
      {point.hasBadge && (
        <div className={`inline-flex items-center gap-2 mt-3 ${align === "left" ? "flex-row-reverse" : ""}`}>
          <div className="h-1.5 w-1.5 rounded-full bg-[#EF4444] animate-pulse" />
          <span className="text-xs text-[#EF4444]/80 font-medium">Problème critique</span>
        </div>
      )}
    </div>
  );
}

const LandingProblem = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasTriggered = useRef(false);
  const [activeStep, setActiveStep] = useState(-1);
  const [lineTarget, setLineTarget] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;

          // Step 0 s'allume tout de suite après le délai initial
          setTimeout(() => setActiveStep(0), INITIAL_DELAY);

          // Steps 1, 2, 3 : la mèche part puis la box s'allume à l'arrivée
          for (let i = 1; i <= 3; i++) {
            const departMeche = INITIAL_DELAY + i * STEP_INTERVAL;
            setTimeout(() => setLineTarget((i / 3) * 100), departMeche);
            setTimeout(() => setActiveStep(i), departMeche + LINE_DURATION);
          }
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeCount = activeStep + 1;

  return (
    <section className="relative py-24 lg:py-32 bg-[#0A0A0F]">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0F] via-[#0F172A]/50 to-[#0A0A0F]" />

      <div className="container relative z-10 mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16 lg:mb-20 max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-wider text-white/40 font-medium mb-4 animate-fade-up">
            Le problème
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight animate-fade-up [animation-delay:100ms]">
            Vous gérez vos chantiers.
            <br />
            <span className="text-white/50">
              Mais connaissez-vous votre marge réelle{" "}
              <span className="text-white">à l'euro près</span> ?
            </span>
          </h2>
        </div>

        {/* Timeline */}
        <div ref={sectionRef} className="relative max-w-3xl mx-auto">
          {/* Ligne centrale : à gauche sur mobile, centrée sur desktop */}
          <div className="absolute left-5 lg:left-1/2 top-0 bottom-0 w-[2px] lg:-translate-x-1/2">
            {/* Fond éteint */}
            <div className="w-full h-full bg-white/[0.06] rounded-full" />
            {/* Ligne allumée */}
            <div
              className="absolute top-0 left-0 w-full rounded-full"
              style={{
                height: `${lineTarget}%`,
                background: "linear-gradient(180deg, #EF4444, #F59E0B, #D4AF37)",
                boxShadow: lineTarget > 2 ? "0 0 6px rgba(245,158,11,0.3)" : "none",
                transition: `height ${LINE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
              }}
            />
            {/* Faisceau (tête lumineuse) */}
            {lineTarget > 1 && lineTarget < 99 && (
              <div
                className="absolute left-[-4px] w-[10px] h-[30px] rounded-full pointer-events-none"
                style={{
                  top: `calc(${lineTarget}% - 15px)`,
                  background: "radial-gradient(ellipse, rgba(255,255,255,0.7), rgba(245,158,11,0.4), transparent)",
                  filter: "blur(2px)",
                  transition: `top ${LINE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              />
            )}
          </div>

          {painPoints.map((point, index) => {
            const isLeft = index % 2 === 0;
            const num = String(index + 1).padStart(2, "0");
            const isLit = index < activeCount;
            const isJustLit = index === activeStep;

            return (
              <div
                key={index}
                className={`py-6 lg:py-8 ${
                  isLeft ? "bg-gradient-to-r from-transparent via-white/[0.01] to-transparent rounded-2xl" : ""
                }`}
              >
                {/* === MOBILE (< lg) === */}
                <div className="lg:hidden flex items-start gap-4 pl-0">
                  <div className="flex-shrink-0 w-10 flex justify-center relative z-10">
                    <div
                      className="w-10 h-10 rounded-[10px] flex items-center justify-center text-xs font-bold border"
                      style={{
                        background: "#131318",
                        borderColor: isLit ? point.color : "rgba(255,255,255,0.06)",
                        color: isLit ? "#FFFFFF" : "rgba(255,255,255,0.3)",
                        boxShadow: isLit ? `0 0 20px ${point.color}30, 0 0 40px ${point.color}10` : "none",
                        transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
                      }}
                    >
                      {num}
                    </div>
                  </div>
                  <div
                    className="flex-1 pt-1"
                    style={{
                      opacity: isLit ? 1 : 0.3,
                      transform: isLit ? "translateY(0)" : "translateY(8px)",
                      transition: "opacity 0.5s ease, transform 0.5s ease",
                    }}
                  >
                    <ItemContent point={point} align="right" />
                  </div>
                </div>

                {/* === DESKTOP (>= lg) === */}
                <div className="hidden lg:grid lg:grid-cols-[1fr_60px_1fr] items-start">
                  {isLeft ? (
                    <>
                      <div
                        className="pr-8"
                        style={{
                          opacity: isLit ? 1 : 0.3,
                          transform: isLit ? "translateY(0)" : "translateY(10px)",
                          transition: "opacity 0.5s ease, transform 0.5s ease",
                        }}
                      >
                        <ItemContent point={point} align="left" />
                      </div>
                      <div className="flex justify-center relative z-10">
                        <div
                          className="w-14 h-14 rounded-[10px] flex items-center justify-center text-lg font-bold border"
                          style={{
                            background: "#131318",
                            borderColor: isLit ? point.color : "rgba(255,255,255,0.06)",
                            color: isLit ? "#FFFFFF" : "rgba(255,255,255,0.3)",
                            boxShadow: isLit ? `0 0 20px ${point.color}30, 0 0 40px ${point.color}10` : "none",
                            transform: isJustLit ? "scale(1.05)" : "scale(1)",
                            transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
                          }}
                        >
                          {num}
                        </div>
                      </div>
                      <div />
                    </>
                  ) : (
                    <>
                      <div />
                      <div className="flex justify-center relative z-10">
                        <div
                          className="w-14 h-14 rounded-[10px] flex items-center justify-center text-lg font-bold border"
                          style={{
                            background: "#131318",
                            borderColor: isLit ? point.color : "rgba(255,255,255,0.06)",
                            color: isLit ? "#FFFFFF" : "rgba(255,255,255,0.3)",
                            boxShadow: isLit ? `0 0 20px ${point.color}30, 0 0 40px ${point.color}10` : "none",
                            transform: isJustLit ? "scale(1.05)" : "scale(1)",
                            transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
                          }}
                        >
                          {num}
                        </div>
                      </div>
                      <div
                        className="pl-8"
                        style={{
                          opacity: isLit ? 1 : 0.3,
                          transform: isLit ? "translateY(0)" : "translateY(10px)",
                          transition: "opacity 0.5s ease, transform 0.5s ease",
                        }}
                      >
                        <ItemContent point={point} align="right" />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Stat 68% */}
        <div
          className="max-w-3xl mx-auto mt-16"
          style={{
            opacity: activeStep >= 3 ? 1 : 0.3,
            transform: activeStep >= 3 ? "translateY(0)" : "translateY(10px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <div className="rounded-2xl bg-[#0F0F14] border border-white/[0.06] p-8 lg:p-10 text-center">
            <div className="text-6xl lg:text-7xl font-bold bg-gradient-to-r from-[#EF4444] to-[#F59E0B] bg-clip-text text-transparent leading-none mb-4">
              68%
            </div>
            <p className="text-base lg:text-lg text-white/50 max-w-md mx-auto leading-relaxed">
              des professionnels du BTP découvrent qu'ils ont perdu de l'argent sur un chantier...{" "}
              <span className="text-[#EF4444] font-semibold">après l'avoir terminé.</span>
            </p>
            <p className="mt-4 text-[10px] text-white/30 uppercase tracking-wider">
              Source : Étude FFB / CAPEB 2024
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LandingProblem;
